'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useCompanyQuery, useCompanyPrefetch } from '@/hooks/use-company-query';
import { useCompanyStore } from '@/lib/stores/company-data-store';

type PageType =
  | 'overview'
  | 'financials'
  | 'financials-income-statement'
  | 'financials-balance-sheet'
  | 'financials-cash-flow'
  | 'financials-growth-ratios'
  | 'dividends'
  | 'ratios'
  | 'segments'
  | 'historical-earnings'
  | 'news'
  | 'earnings-call';

const getPagePath = (pageType: PageType): string => {
  switch (pageType) {
    case 'overview':
      return '';
    case 'financials':
      return '/financials';
    case 'financials-income-statement':
      return '/financials/income-statement';
    case 'financials-balance-sheet':
      return '/financials/balance-sheet';
    case 'financials-cash-flow':
      return '/financials/cash-flow';
    case 'financials-growth-ratios':
      return '/financials/growth-ratios';
    case 'dividends':
      return '/dividends';
    case 'ratios':
      return '/ratios';
    case 'segments':
      return '/segments';
    case 'historical-earnings':
      return '/historical-earnings';
    case 'news':
      return '/news';
    case 'earnings-call':
      return '/earnings-call';
    default:
      return '';
  }
};

export function useCompanyPage(symbol: string, pageType: PageType) {
  const [noteSheetOpen, setNoteSheetOpen] = useState(false);
  const router = useRouter();

  // Store and Query Hooks
  const { tabs, activeTab, addTab, removeTab, setActiveTab } =
    useCompanyStore();
  const { data, isLoading, error } = useCompanyQuery(activeTab || symbol);
  const prefetchData = useCompanyPrefetch();

  // Add the initial symbol to the store and set as active
  useEffect(() => {
    const upperSymbol = symbol.toUpperCase();

    // Check if we're navigating due to tab deletion
    const urlParams = new URLSearchParams(window.location.search);
    const fromTabDeletion = urlParams.get('from') === 'tab-deletion';

    console.log('useCompanyPage effect:', {
      symbol: upperSymbol,
      fromTabDeletion,
      tabExists: tabs.some((tab) => tab.symbol === upperSymbol),
      currentTabs: tabs.map((t) => t.symbol),
      activeTab,
    });

    // Only add tab if NOT coming from tab deletion
    if (
      !fromTabDeletion &&
      symbol &&
      !tabs.some((tab) => tab.symbol === upperSymbol)
    ) {
      console.log('Adding tab for:', upperSymbol);
      addTab({
        symbol: upperSymbol,
        companyName: upperSymbol,
      });
    } else {
      console.log('NOT adding tab because:', {
        fromTabDeletion,
        symbol: !!symbol,
        tabExists: tabs.some((tab) => tab.symbol === upperSymbol),
      });
    }

    // Set active tab only if it's different from URL symbol
    if (symbol && activeTab !== upperSymbol) {
      setActiveTab(upperSymbol);
    }

    // Clean up the URL parameter to keep URLs clean
    if (fromTabDeletion) {
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol]);

  // Handlers
  const handleSearchSelect = (newSymbol: string) => {
    const upperSymbol = newSymbol.toUpperCase();
    addTab({
      symbol: upperSymbol,
      companyName: upperSymbol,
    });
    const pagePath = getPagePath(pageType);
    router.push(`/company/${upperSymbol}${pagePath}`);
  };

  const handleTabHover = (tabSymbol: string) => {
    if (tabSymbol !== activeTab) {
      prefetchData(tabSymbol);
    }
  };

  const handleTabSelect = (tabSymbol: string) => {
    setActiveTab(tabSymbol);
  };

  const handleTabRemove = (tabSymbol: string) => {
    removeTab(tabSymbol);
  };

  const handleNotesOpen = () => {
    setNoteSheetOpen(true);
  };

  const handleNotesClose = () => {
    setNoteSheetOpen(false);
  };

  return {
    // State
    noteSheetOpen,
    tabs,
    activeTab,
    data,
    isLoading,
    error,

    // Handlers
    handleSearchSelect,
    handleTabHover,
    handleTabSelect,
    handleTabRemove,
    handleNotesOpen,
    handleNotesClose,
  };
}
